import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polygon, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png'
});

// Custom vehicle icon
const createVehicleIcon = (status) => {
  const colors = {
    online: '#22c55e',
    offline: '#9ca3af',
    moving: '#3b82f6',
    idle: '#f59e0b',
    alert: '#ef4444'
  };

  return L.divIcon({
    className: 'custom-vehicle-icon',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        background: ${colors[status] || colors.online};
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
          <path d="M15 18H9"/>
          <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/>
          <circle cx="17" cy="18" r="2"/>
          <circle cx="7" cy="18" r="2"/>
        </svg>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18]
  });
};

// Zone colors by category
const ZONE_COLORS = {
  warehouse: { fill: '#22c55e', stroke: '#16a34a' },
  restricted: { fill: '#ef4444', stroke: '#dc2626' },
  delivery: { fill: '#f59e0b', stroke: '#d97706' },
  service: { fill: '#3b82f6', stroke: '#2563eb' }
};

// Map click handler for drawing
const MapClickHandler = ({ isDrawing, drawingType, onPointAdd }) => {
  useMapEvents({
    click: (e) => {
      if (isDrawing) {
        onPointAdd({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    }
  });
  return null;
};

// Map bounds updater
const MapBoundsUpdater = ({ vehicles, zones }) => {
  const map = useMap();

  useEffect(() => {
    const points = [];

    vehicles.forEach((v) => {
      if (v.location?.lat && v.location?.lng) {
        points.push([v.location.lat, v.location.lng]);
      }
    });

    zones.forEach((z) => {
      if (z.type === 'circle' && z.center) {
        points.push([z.center.lat, z.center.lng]);
      } else if (z.type === 'polygon' && z.points?.length > 0) {
        z.points.forEach((p) => points.push([p.lat, p.lng]));
      }
    });

    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [vehicles, zones, map]);

  return null;
};

// Main Leaflet Map Component
const LeafletMap = ({
  vehicles = [],
  zones = [],
  selectedVehicle,
  onVehicleSelect,
  selectedZone,
  onZoneSelect,
  isDrawing = false,
  drawingType = 'circle',
  drawingPoints = [],
  onPointAdd,
  onMapClick,
  center = { lat: -23.5505, lng: -46.6333 },
  zoom = 12,
  className = ''
}) => {const { t } = useTranslation("wms");
  const mapRef = useRef(null);

  return (
    <MapContainer
      ref={mapRef}
      center={[center.lat, center.lng]}
      zoom={zoom}
      className={`w-full h-full ${className}`}
      style={{ minHeight: '400px' }}>
      
      {/* Tile Layer - OpenStreetMap */}
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      
      
      {/* Click Handler for Drawing */}
      <MapClickHandler
        isDrawing={isDrawing}
        drawingType={drawingType}
        onPointAdd={onPointAdd} />
      
      
      {/* Bounds Updater */}
      <MapBoundsUpdater vehicles={vehicles} zones={zones} />
      
      {/* Render Zones */}
      {zones.map((zone) => {
        const colors = ZONE_COLORS[zone.category] || ZONE_COLORS.service;
        const isSelected = selectedZone?.id === zone.id;

        if (zone.type === 'circle' && zone.center) {
          return (
            <Circle
              key={zone.id}
              center={[zone.center.lat, zone.center.lng]}
              radius={zone.radius || 500}
              pathOptions={{
                color: colors.stroke,
                fillColor: colors.fill,
                fillOpacity: isSelected ? 0.4 : 0.2,
                weight: isSelected ? 3 : 2
              }}
              eventHandlers={{
                click: () => onZoneSelect?.(zone)
              }}>
              
              <Popup>
                <div className="text-sm">
                  <strong>{zone.name}</strong>
                  <br />{t("category", "Category:")}
                  {zone.category}
                  <br />{t("radius", "Radius:")}
                  {zone.radius}m
                </div>
              </Popup>
            </Circle>);

        }

        if (zone.type === 'polygon' && zone.points?.length > 2) {
          return (
            <Polygon
              key={zone.id}
              positions={zone.points.map((p) => [p.lat, p.lng])}
              pathOptions={{
                color: colors.stroke,
                fillColor: colors.fill,
                fillOpacity: isSelected ? 0.4 : 0.2,
                weight: isSelected ? 3 : 2
              }}
              eventHandlers={{
                click: () => onZoneSelect?.(zone)
              }}>
              
              <Popup>
                <div className="text-sm">
                  <strong>{zone.name}</strong>
                  <br />{t("category", "Category:")}
                  {zone.category}
                  <br />{t("points", "Points:")}
                  {zone.points.length}
                </div>
              </Popup>
            </Polygon>);

        }

        return null;
      })}
      
      {/* Drawing Preview */}
      {isDrawing && drawingPoints.length > 0 &&
      <>
          {drawingType === 'circle' && drawingPoints.length === 1 &&
        <Circle
          center={[drawingPoints[0].lat, drawingPoints[0].lng]}
          radius={500}
          pathOptions={{
            color: '#8b5cf6',
            fillColor: '#8b5cf6',
            fillOpacity: 0.3,
            dashArray: '5, 5'
          }} />

        }
          {drawingType === 'polygon' && drawingPoints.length > 1 &&
        <Polygon
          positions={drawingPoints.map((p) => [p.lat, p.lng])}
          pathOptions={{
            color: '#8b5cf6',
            fillColor: '#8b5cf6',
            fillOpacity: 0.3,
            dashArray: '5, 5'
          }} />

        }
          {/* Show points while drawing */}
          {drawingPoints.map((point, idx) =>
        <Marker
          key={`draw-${idx}`}
          position={[point.lat, point.lng]}
          icon={L.divIcon({
            className: 'drawing-point',
            html: `<div style="width:12px;height:12px;background:#8b5cf6;border:2px solid white;border-radius:50%;"></div>`,
            iconSize: [12, 12],
            iconAnchor: [6, 6]
          })} />

        )}
        </>
      }
      
      {/* Render Vehicles */}
      {vehicles.map((vehicle) => {
        if (!vehicle.location?.lat || !vehicle.location?.lng) return null;

        return (
          <Marker
            key={vehicle.id}
            position={[vehicle.location.lat, vehicle.location.lng]}
            icon={createVehicleIcon(vehicle.status)}
            eventHandlers={{
              click: () => onVehicleSelect?.(vehicle)
            }}>
            
            <Popup>
              <div className="text-sm">
                <strong>{vehicle.name}</strong>
                <br />{t("status", "Status:")}
                {vehicle.status}
                <br />{t("speed", "Speed:")}
                {vehicle.speed || 0}{t("kmh", "km/h")}
                {vehicle.driver && <><br />{t("driver", "Driver:")}{vehicle.driver}</>}
              </div>
            </Popup>
          </Marker>);

      })}
    </MapContainer>);

};

export default LeafletMap;